import * as React from "react";
import { connect } from "react-redux";

import { enAU } from "date-fns/locale";
import { orderBy, range, uniq } from "lodash";
import { DateTime } from "luxon";

import { DatePicker, DateRangePicker } from "@bjarkehs/react-nice-dates";
import { Alert, Button, Checkbox, Grid, MultiSelect, Radio, Select, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { bindActionCreators } from "redux";
import { createUnavailability } from "../../data/actions/availabilityActions";
import ShiftApi from "../../data/api/shiftsApi";
import {
  toDateTime,
  toDateTimeNullable,
  toHumanDuration,
} from "../../util/date+helper";

import { IAvailability } from "../../types/availabilities/Availabilities";
import { AvailabilityType } from "../../types/availabilities/AvailabilityType";
import { IShift } from "../../types/shifts/Shift";

import {
  IRecurringAvailability,
  IRecurringSpecifier,
  RecurrenceType,
} from "../../types/availabilities/RecurringAvailability";
import { CenteredLoader } from "../common/loader";

export enum DateRangeFocus {
  startDate = "startDate",
  endDate = "endDate",
}

const DEFAULT_RECURRENCE = {
  type: RecurrenceType.weekly,
  separationCount: 1,
};

export interface ICreateAvailabilityFormProps {
  person: string;
  toggle?: () => void | null;

  isRecurring: boolean;

  container?: React.ReactElement | null;

  onSave: () => void;
  onCancel: () => void;

  actions: {
    createUnavailability: (
      availability: Partial<IAvailability>,
    ) => Promise<IAvailability>;
  };
}

const dateModifier = {
  disabled: (date) => date < new Date(),
};

const timeValues: (options: {
  minTime?: DateTime | null;
  maxTime?: DateTime | null;
  intervals?: number;
}) => string[] = ({ minTime, maxTime, intervals }) => {
  const intv = intervals || 15;
  const startMinutes = minTime ? minTime?.hour * 60 + minTime.minute + intv : 0;
  const finishMinutes = maxTime ? maxTime?.hour * 60 + maxTime.minute : 1425;
  return range(startMinutes, finishMinutes, intv).map(
    (mn) =>
      `${Math.floor(mn / 60)
        .toString()
        .padStart(2, "0")}:${(mn % 60).toString().padStart(2, "0")}`,
  );
};
// range(24)
//     .reduce((prev, hr) => [
//         ...prev,
//         ...range(0, 60, 15)
//             .map(mn => `${hr.toString().padStart(2, '0')}:${mn.toString().padStart(2, '0')}`)
//     ], [])

interface ITimeSelectorProps {
  value?: string;
  minTime?: DateTime | null;
  maxTime?: DateTime | null;
  placeholder?: string;
  name?: string;
  disabled?: boolean;
  onChange: (value: string | null) => void;
}

const TimeSelector: React.FunctionComponent<ITimeSelectorProps> = ({
  value,
  minTime,
  maxTime,
  ...props
}) => {
  const tvs = timeValues({ minTime, maxTime });
  const data = [];

  if (!value && props.placeholder) {
    data.push({ value: "", label: props.placeholder });
  }

  tvs.forEach((tv) => {
    data.push({ value: tv, label: tv });
  });

  if (!!value && !tvs.includes(value)) {
    data.push({ value: value, label: value });
  }

  return (
    <Select
      value={value || ""}
      placeholder={props.placeholder}
      name={props.name}
      disabled={props.disabled}
      data={data}
      onChange={(val) => props.onChange(val)}
    />
  );
};

const addToArray = (ar, el) => uniq([...(ar || []), el]);
const removeFromArray = (ar, el) => (ar || []).filter((r) => r !== el);

const CreateAvailabilityForm: React.FunctionComponent<
  ICreateAvailabilityFormProps
> = (props: ICreateAvailabilityFormProps) => {
  const [disabled, setDisabled] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const [isAllDay, setIsAllDay] = React.useState<boolean>(true);
  const [availability, setAvailability] = React.useState<
    Partial<IAvailability>
  >({ type: AvailabilityType.personal });
  const setAvail = (changes: Partial<IAvailability>) =>
    setAvailability({ ...availability, ...changes });
  const setRecurAvail = (changes: Partial<IRecurringAvailability>) =>
    setAvail(changes);
  const [isMakingCoveredShiftRequest, setIsMakingShiftRequest] =
    React.useState(false);
  const [coveredShiftError, setCoveredShiftsError] = React.useState<
    string | null
  >(null);
  const [coveredShifts, setCoveredShifts] = React.useState<null | IShift[]>(
    null,
  );
  const [venues, setVenues] = React.useState<{ [key: string]: string } | null>(
    null,
  );

  // Add CSS for date picker positioning (dark mode handled in global styles.css)
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'availability-datepicker-styles';
    style.textContent = `
      .nice-dates-popover {
        transform: scale(0.9) !important;
        max-width: min(320px, 90vw) !important;
        left: 8px !important;
        right: auto !important;
      }
      .nice-dates-navigation,
      .nice-dates-week-header {
        font-size: 0.9em;
      }
      .nice-dates-day {
        font-size: 0.85em;
      }
      @media (max-width: 768px) {
        .nice-dates-popover {
          transform: scale(0.85) !important;
          max-width: min(280px, 90vw) !important;
        }
      }
    `;

    // Remove existing style if present
    const existing = document.getElementById('availability-datepicker-styles');
    if (existing) {
      existing.remove();
    }

    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById('availability-datepicker-styles');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  const saveAvail = (e) => {
    e.preventDefault();

    if (toDateTime(availability.start) >= toDateTime(availability.finish))
      return setSaveError("Start must be less than finish date");

    availability.isRecurring = props.isRecurring;

    setIsSaving(true);
    setDisabled(true);
    setSaveError(null);

    props.actions
      .createUnavailability(availability)
      .then((avs) => {
        setIsSaving(false);
        props.onSave();
      })
      .catch((er) => {
        setIsSaving(false);
        setDisabled(false);
        setSaveError(er.message);
      });
  };

  const isRecurring = props.isRecurring || false;
  const recurringAvail: Partial<IRecurringAvailability> =
    availability as IRecurringAvailability;
  const showShiftTitle =
    coveredShifts || isMakingCoveredShiftRequest || coveredShiftError;

  const onTimeChange = (name) => (timeValue: string | null) => {
    const time = timeValue || "";
    if (!time.includes(":")) {
      setAvail({ [name]: undefined });
      return;
    }
    // merge date with start

    const availDate: DateTime =
      toDateTimeNullable(availability[name]) || DateTime.local();

    // now I'll admit this probably isn't the BEST way to do this,
    // buuttttt, it's a predictable format
    const [hours, minutes] = time.split(":");

    const updatedDate = availDate
      .set({
        hour: new Number(hours).valueOf(),
        minute: new Number(minutes).valueOf(),
      })
      .toJSDate();

    const start = name == "start" ? updatedDate : (availability.start as Date);
    const finish =
      name == "finish" ? updatedDate : (availability.finish as Date);
    setAvail({ [name]: updatedDate });
    if (start && finish) onDateRangeChange(start, finish);
  };

  const onDateChange = (name) => (date) => {
    // merge date with time
    const availDate = toDateTimeNullable(availability[name]);
    const updatedDate = toDateTimeNullable(date);
    if (!updatedDate) return;

    const hasTime = !!availDate;

    let newDate = (availDate || updatedDate).set({
      year: updatedDate.year,
      month: updatedDate.month,
      day: updatedDate.day,
    });

    let start = availability.start as Date;
    let finish = availability.finish as Date;

    if (name == "start" && (isAllDay || !hasTime)) {
      newDate = newDate.startOf("day");
      start = newDate.toJSDate();
    } else if (name == "finish" && (isAllDay || !hasTime)) {
      newDate = newDate.endOf("day");
      finish = newDate.toJSDate();
    }

    setAvail({ [name]: newDate.toJSDate() });
    if (start && finish) onDateRangeChange(start, finish);
  };

  const onDateRangeChange = (start: Date, finish: Date) => {
    if (!(start && finish)) return;

    setIsMakingShiftRequest(true);
    if (coveredShiftError) setCoveredShiftsError(null);
    ShiftApi.getMyShiftsInRange(start, finish)
      .then(({ shifts, venues }) => {
        setCoveredShifts(shifts);
        setVenues(
          venues.reduce((prev, v) => ({ ...prev, [v._id]: v.name }), {}),
        );
        setIsMakingShiftRequest(false);
      })
      .catch((er) => {
        setCoveredShiftsError(er.message);
        setIsMakingShiftRequest(false);
      });
  };

  const s = toDateTimeNullable(availability.start); // || DateTime.utc().plus({ days: 7 }).startOf("day")
  const f = toDateTimeNullable(availability.finish); // || DateTime.utc().plus({ days: 8 }).startOf("day")

  const isValid =
    availability.start &&
    availability.finish &&
    availability.start < availability.finish &&
    availability.type &&
    (!isRecurring ||
      (isRecurring &&
        availability.finish &&
        toDateTime(availability.start).day !=
        toDateTime(availability.finish).day));

  const inner = (
    <Stack gap="md">
      <Radio.Group
        value={availability.available ? "available" : "unavailable"}
        onChange={(value) => setAvail({ available: value === "available" })}
      >
        <Grid gutter="xs">
          <Grid.Col span={6}>
            <Radio
              value="available"
              label={
                <div >
                  <Text size="sm" fw={600}>Available</Text>
                  <Text size="xs" c="dimmed">You are available</Text>
                </div>
              }
              styles={{
                radio: { cursor: "pointer" },
                label: {
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: availability.available ? "2px solid var(--mantine-color-blue-6)" : "2px solid var(--mantine-color-gray-3)",
                  backgroundColor: availability.available ? "var(--mantine-color-blue-light)" : "transparent",
                  transition: "all 0.2s ease",
                  display: "block",
                  width: "100%",
                  marginLeft: "10px",

                },
              }}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <Radio
              value="unavailable"
              label={
                <div style={{}}>
                  <Text size="sm" fw={600}>Unavailable</Text>
                  <Text size="xs" c="dimmed">You are unavailable</Text>
                </div>
              }
              styles={{
                radio: { cursor: "pointer" },
                label: {
                  cursor: "pointer",
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: !availability.available ? "2px solid var(--mantine-color-blue-6)" : "2px solid var(--mantine-color-gray-3)",
                  // backgroundColor: !availability.available ? "var(--mantine-color-blue-light)" : "transparent",
                  transition: "all 0.2s ease",
                  marginLeft: "10px",
                  display: "block",
                  width: "100%",
                },
              }}
            />
          </Grid.Col>
        </Grid>
      </Radio.Group>

      {!availability.available && (
        <Select
          name="type"
          id="availabilityType"
          label="Reason"
          placeholder="Select a reason"
          value={availability.type}
          onChange={(value) =>
            setAvail({
              type: AvailabilityType.fromString(value),
            })
          }
          disabled={disabled}
          data={AvailabilityType.forManual().map((t) => ({
            value: t,
            label: AvailabilityType.toString(t)
          }))}
        />
      )}

      <AvailabilityDateTimeSelector
        isRecurring={isRecurring}
        disabled={disabled}
        availability={availability}
        setAvail={setAvail}
        isAllDay={isAllDay}
        setIsAllDay={setIsAllDay}
        onDateRangeChange={onDateRangeChange}
        onDateChange={onDateChange}
        onTimeChange={onTimeChange}
        onAvailChange={setRecurAvail}
      />

      {isMakingCoveredShiftRequest && <CenteredLoader />}
      {coveredShiftError && <Alert color="red">{coveredShiftError}</Alert>}
      {!!coveredShifts && coveredShifts.length > 0 && (
        <div style={{ padding: "8px 12px", borderRadius: "8px" }}>
          <Text size="sm" fw={500} mb={4}>Affected Shifts</Text>
          <Stack gap={4}>
            {coveredShifts.map((s) => (
              <Text key={s._id} size="xs" c="dimmed">
                • {s.name} ({s.sessions.length} session{s.sessions.length !== 1 ? "s" : ""})
              </Text>
            ))}
          </Stack>
        </div>
      )}

      {coveredShifts && venues && (
        <div>
          <Text size="sm" fw={500} mb="xs">Venues</Text>
          <Checkbox
            name="avail-all-venues"
            label="All venues"
            checked={!availability.venues || availability.venues.length == 0}
            onChange={() => setAvail({ ...availability, venues: undefined })}
            disabled={disabled}
            mb="xs"
          />
          <Stack gap={6}>
            {orderBy(Object.keys(venues), (v) => venues[v]).map((v) => (
              <Checkbox
                key={v}
                name={`avail-venue-${v}`}
                value={v}
                label={venues[v]}
                size="sm"
                checked={availability.venues ? availability.venues.some((ven) => ven === v) : false}
                onChange={(e) =>
                  setAvail({
                    ...availability,
                    venues: (e.currentTarget.checked ? addToArray : removeFromArray)(availability.venues, v),
                  })
                }
                disabled={disabled}
              />
            ))}
          </Stack>
        </div>
      )}

      <Textarea
        name="comment"
        id="comment"
        label="Comments (optional)"
        placeholder="Add any additional details..."
        value={availability.comment}
        onChange={(e) =>
          setAvail({ ...availability, comment: e.currentTarget.value })
        }
        minRows={2}
        disabled={disabled}
      />

      {isSaving && <CenteredLoader />}
      {saveError && <Alert color="red">{saveError}</Alert>}

      <Grid gutter="xs">
        {!!props.toggle && (
          <Grid.Col span={6}>
            <Button
              variant="default"
              onClick={() => props?.toggle?.()}
              disabled={disabled}
              fullWidth
            >
              Cancel
            </Button>
          </Grid.Col>
        )}
        <Grid.Col span={!!props.toggle ? 6 : 12}>
          <Button
            onClick={saveAvail}
            disabled={disabled || !isValid}
            fullWidth
          >
            Submit
          </Button>
        </Grid.Col>
      </Grid>
    </Stack>
  );
  // @ts-ignore
  return React.createElement(props.container || "div", {
    style: { padding: "16px" },
    children: inner,
  });
};

function parsePositiveIntOrPrevious(
  str: number | string,
  previous?: number,
): number | undefined {
  if (str?.toString()?.length == 0) return undefined;
  const n = Math.floor(Number(str));
  if (n !== Infinity && n >= 0) {
    return Number(str);
  }
  return previous;
}

interface IAvailabilityDateTimeSelectorProps {
  isRecurring: boolean;
  disabled: boolean;

  availability: Partial<IAvailability>;
  setAvail: Function;

  isAllDay: boolean;
  setIsAllDay: Function;

  onDateChange: (name: string) => (date: Date) => void;
  onTimeChange: (name: string) => (time: string | null) => void;
  onDateRangeChange: (start: Date, finish: Date) => void;
  onAvailChange: (avail: Partial<IRecurringAvailability>) => void;
}

const AvailabilityDateTimeSelector: React.FunctionComponent<
  IAvailabilityDateTimeSelectorProps
> = ({ isRecurring, isAllDay, availability, ...rest }) => {
  const s = toDateTimeNullable(availability.start);
  const f = toDateTimeNullable(availability.finish);

  const allDayCheckbox = (
    <Checkbox
      name="allday"
      id="allday"
      label="All day"
      checked={isAllDay}
      onChange={(e) => {
        const useTime = e.currentTarget.checked;
        rest.setIsAllDay(useTime);
        if (useTime && availability.start) {
          const start = toDateTimeNullable(availability.start)
            ?.startOf("day")
            ?.toJSDate();
          const finish = toDateTimeNullable(availability.finish)
            ?.endOf("day")
            ?.toJSDate();
          if (start && finish) {
            rest.setAvail({ start, finish });
            rest.onDateRangeChange(start, finish);
          }
        }
      }}
      disabled={rest.disabled}
      mb="xs"
    />
  );

  const limitTimeSelectors = isRecurring || !f || s?.hasSame(f, "day") || false;

  const startTimeSelector = (
    <TimeSelector
      name="start"
      placeholder="Start time"
      value={s?.toFormat("HH:mm")}
      onChange={rest.onTimeChange("start")}
      disabled={rest.disabled || isAllDay}
      maxTime={limitTimeSelectors ? f : undefined}
    />
  );

  const finishTimeSelector = (
    <TimeSelector
      name="finish"
      placeholder="Finish time"
      value={f?.toFormat("HH:mm")}
      onChange={rest.onTimeChange("finish")}
      disabled={rest.disabled || !availability.start || isAllDay}
      minTime={limitTimeSelectors ? s : undefined}
    />
  );

  if (isRecurring) {
    const recurAvail = availability as IRecurringAvailability;
    const startDateSelector = (
      <DatePicker
        date={s?.toJSDate()}
        onDateChange={rest.onDateChange("start")}
        locale={enAU}
        modifiers={dateModifier}
      >
        {({ inputProps, focused }) => (
          <TextInput
            {...inputProps}
            label="Start"
            type="text"
            className={`input${focused ? " -focused" : ""}`}
            placeholder="Click to select a date"
            disabled={rest.disabled}
          />
        )}
      </DatePicker>
    );

    const finishDateSelector = (
      <DatePicker
        date={f?.toJSDate()}
        onDateChange={rest.onDateChange("finish")}
        locale={enAU}
        minimumDate={s?.startOf("day")?.plus({ day: 1 })?.toJSDate()}
      >
        {({ inputProps, focused }) => (
          <TextInput
            {...inputProps}
            label="End recurring"
            type="text"
            className={`input${focused ? " -focused" : ""}`}
            placeholder="Click to select a date"
            disabled={!s || rest.disabled}
          />
        )}
      </DatePicker>
    );

    let timeDiff: number | undefined;
    if (s && f) {
      const day = { year: 2000, month: 1, day: 1 };
      const stime = s.set(day);
      const ftime = f.set(day);
      timeDiff = ftime.diff(stime, "seconds").seconds;
    }

    const availUnavail = availability.available ? "availability" : "unavailability";

    return (
      <Stack gap="sm">
        {allDayCheckbox}
        {s && f && s >= f && (
          <Alert color="red">Start date is greater than end date</Alert>
        )}
        <Grid gutter="xs">
          <Grid.Col span={6}>{startTimeSelector}</Grid.Col>
          <Grid.Col span={6}>{finishTimeSelector}</Grid.Col>
        </Grid>
        {timeDiff && (
          <Text size="xs" c="dimmed" ta="center">
            Recurring {availability.available ? "availability" : "unavailability"} starts at {s?.toFormat("hh:mm a")} for {toHumanDuration(timeDiff)}
          </Text>
        )}
        <Grid gutter="xs">
          <Grid.Col span={6}>{startDateSelector}</Grid.Col>
          <Grid.Col span={6}>{finishDateSelector}</Grid.Col>
        </Grid>
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            borderRadius: "8px",
            borderLeft: "3px solid var(--mantine-color-blue-6)",
          }}
        >
          <RecurrenceSpecifier
            recurrence={recurAvail.recurrence}
            onChange={(recur) =>
              rest.onAvailChange({
                recurrence: {
                  ...DEFAULT_RECURRENCE,
                  timezone: DateTime.local().zoneName,
                  // @ts-ignore
                  ...(availability.recurrence || {}),
                  ...recur,
                },
              })
            }
          />
        </div>
        <Text size="xs" c="dimmed" ta="center">
          This {availUnavail} will be added in the {DateTime.local().zoneName} timezone.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {allDayCheckbox}
      {s && f && s >= f && (
        <Alert color="red">Start date is greater than end date</Alert>
      )}
      <DateRangePicker
        startDate={s?.toJSDate()}
        endDate={f?.toJSDate()}
        onStartDateChange={rest.onDateChange("start")}
        onEndDateChange={rest.onDateChange("finish")}
        locale={enAU}
        modifiers={dateModifier}
      >
        {({ startDateInputProps, endDateInputProps, focus }) => (
          <Stack gap="xs">
            <Text size="sm" fw={500}>Start</Text>
            <Grid gutter="xs">
              <Grid.Col span={6}>
                <div style={{ position: 'relative' }}>
                  <TextInput
                    className={`input${focus === DateRangeFocus.startDate ? " -focused" : ""}`}
                    {...startDateInputProps}
                    type="text"
                    placeholder="Start date"
                    disabled={rest.disabled}
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={6}>{startTimeSelector}</Grid.Col>
            </Grid>
            <Text size="sm" fw={500}>Finish</Text>
            <Grid gutter="xs">
              <Grid.Col span={6}>
                <div style={{ position: 'relative' }}>
                  <TextInput
                    className={`input${focus === DateRangeFocus.endDate ? " -focused" : ""}`}
                    {...endDateInputProps}
                    type="text"
                    placeholder="End date"
                    disabled={rest.disabled}
                  />
                </div>
              </Grid.Col>
              <Grid.Col span={6}>{finishTimeSelector}</Grid.Col>
            </Grid>
          </Stack>
        )}
      </DateRangePicker>
    </Stack>
  );
};

const RecurrenceSpecifier: React.FunctionComponent<{
  recurrence?: IRecurringSpecifier | null;
  onChange: (specificer: Partial<IRecurringSpecifier>) => void;
}> = ({ recurrence, onChange }) => {
  const spec: Partial<IRecurringSpecifier> = {
    ...DEFAULT_RECURRENCE,
    ...(recurrence || {}),
  };

  const sepCount = Math.max(spec.separationCount || 1, 1);

  let innerSetter: React.ReactElement | null = null;

  if (!spec?.type || spec.type == RecurrenceType.daily) {
    const dayText = sepCount == 1 ? "day" : `${sepCount} days`;
    innerSetter = (
      <div>
        <Text size="sm" fw={500} mb={4}>Every {dayText}</Text>
        <TextInput
          id="separationCount"
          type="number"
          value={sepCount}
          onChange={(e) =>
            onChange({
              separationCount: parsePositiveIntOrPrevious(
                e.currentTarget.value,
                spec?.separationCount,
              ),
            })
          }
          size="sm"
        />
      </div>
    );
  } else if (spec.type == RecurrenceType.weekly) {
    const weekText = sepCount == 1 ? "week" : `${sepCount} weeks`;
    const days = RecurrenceType.days();
    const onText = spec?.dayOfWeek?.length
      ? ` on ${spec?.dayOfWeek?.map((d) => days[d]).join(", ")}`
      : "";
    innerSetter = (
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={500} mb={4}>Every {weekText}{onText}</Text>
          <TextInput
            id="separationCount"
            type="number"
            value={sepCount}
            onChange={(e) =>
              onChange({
                separationCount: parsePositiveIntOrPrevious(
                  e.currentTarget.value,
                  spec?.separationCount,
                ),
              })
            }
            size="sm"
          />
        </div>
        <MultiSelect
          label="Days"
          id="dayOfWeek"
          value={spec.dayOfWeek?.map((a) => a.toString())}
          onChange={(values) => {
            const dayValues = (values || []).map((v) => Number(v));
            onChange({ dayOfWeek: dayValues });
          }}
          data={RecurrenceType.dayOfTheWeek().map((a) => ({
            value: a.value.toString(),
            label: a.name
          }))}
          size="sm"
        />
      </Stack>
    );
  } else if (spec.type == RecurrenceType.monthly) {
    const monthText = sepCount == 1 ? "month" : `${sepCount} months`;
    innerSetter = (
      <div>
        <Text size="sm" fw={500} mb={4}>Every {monthText}</Text>
        <TextInput
          id="separationCount"
          type="number"
          value={sepCount}
          onChange={(e) =>
            onChange({
              separationCount: parsePositiveIntOrPrevious(
                e.currentTarget.value,
                spec?.separationCount,
              ),
            })
          }
          size="sm"
        />
      </div>
    );
  }

  return (
    <Stack gap="sm">
      <Select
        label="Repeat"
        id="recurrenceType"
        value={spec?.type}
        onChange={(value) =>
          onChange({ type: RecurrenceType[value] })
        }
        data={RecurrenceType.allOrdered().map((t) => ({
          value: t,
          label: RecurrenceType.toString(t)
        }))}
        size="sm"
      />
      {innerSetter}
    </Stack>
  );
};

function mapStateToProps(state, props) {
  return {};
}

function mapDispatchToProps(dispatch): Partial<ICreateAvailabilityFormProps> {
  return {
    // @ts-ignore
    actions: bindActionCreators({ createUnavailability }, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CreateAvailabilityForm);
